import {
  Paper,
  Box,
  Typography,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

import MyLocationIcon from '@mui/icons-material/MyLocation';

import {
  useEffect,
  useRef,
  useState
} from "react";

import * as d3 from "d3";
import { getMetricParamName, getMetricUnits } from "./RTFOMetricsPanel";
import { getSeismicEventColor } from "./RTFOMapWithWellbore";

function ColorScaleRectangle({
  colorScale,
  height,
  width,
  range,
  darkMode
}) {
  const svgRef = useRef();

  useEffect(() => {
    // Select the SVG element
    svgRef.current.innerHTML = "";
    const svg = d3.select(svgRef.current);
    // Create a color scale
    const newColorScale = d3.scaleSequential(colorScale)
      .domain([height, 0]);

    // Create a rectangle for each step in the color scale
    const stepCount = height;
    const stepSize = height / stepCount; // Adjust stepSize based on height

    svg.selectAll('rect')
      .data(d3.range(stepCount))
      .join('rect')
      .attr('x', 0)                    // All rectangles start at x = 0
      .attr('y', d => {
        return d * stepSize;
      })    // Adjust y position for vertical layout
      .attr('width', width)            // Width of each rectangle
      .attr('height', stepSize)        // Height based on stepSize
      .attr('fill', d => {
        return newColorScale(d);
      });

    svg.attr("overflow", "visible");

    // Add a vertical color legend (optional)
    const axisScale = d3.scaleLinear()
      .domain(range)
      .range([height, 0]);  // Reverse the range for vertical axis

    const axisLeft = d3.axisLeft(axisScale)
      .ticks(height / 20);

    svg.append('g')
      .attr("color", darkMode ? "white" : "black")
      .call(axisLeft);

  }, [height, width, range, colorScale, darkMode]);

  return (
    <svg
      ref={svgRef}
      width={width + 10}
      height={height + 20}
    ></svg>
  );
};

function RTFOWellboreUIColorScaleVisualization({
  colorScale,
  range,
  metric,
  units,
  darkMode
}) {
  const containerRef = useRef();
  const [colorScaleRectangleHeight, setColorScaleRectangleHeight] = useState(500);

  useEffect(() => {
    if (containerRef.current !== undefined) {
      setColorScaleRectangleHeight(containerRef.current.offsetHeight);
    };
  }, [containerRef]);

  return (
    <Box
      sx={{
        height: "35%",
        position: "absolute",
        top: "20px",
        right: "20px",
      }}
      ref={containerRef}
    >
      <Typography
        fontSize="small"
        color={darkMode ? "white" : "black"}
      >
        Color Scale
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          flexDirection: "row",
        }}
      >
        <ColorScaleRectangle
          width={40}
          height={colorScaleRectangleHeight}
          range={range}
          colorScale={colorScale}
          darkMode={darkMode}
        />
        <Typography
          color={darkMode ? "white" : "black"}
          fontSize="small"
          padding={0}
          margin={0}
          sx={{
            display: "flex",
            position: "absolute",
            justifyContent: "center",
            alignItems: "center",
            right: "-150%",
            transform: 'translate(0%, -50%) rotate(-90deg)',
            top: "50%",
            width: "200px"
          }}
        >{metric} {getMetricUnits(getMetricParamName(metric), units) ? `(${getMetricUnits(getMetricParamName(metric), units)})` : ''}</Typography>
      </Box>
    </Box>
  );
};

export default function RTFOWellbore3DUI({
  colorScale,
  range,
  setHeightMultiplier,
  heightMultiplier,
  setWidthMultiplier,
  verticalScaleMultiplier,
  setVerticalScaleMultiplier,
  widthMultiplier,
  hideFracGeometry,
  setHideFracGeometry,
  metric,
  units,
  setMetric,
  allMetrics,
  seismicityFilter,
  setSeismicityFilter,
  resetCamera,
  isFDI,
  darkMode
}) {

  return (
    <>
      <Tooltip
        title="Reset Camera"
        sx={{
          position: "absolute",
          top: "50%",
          right: "40px"
        }}
      >
        <IconButton
          onClick={resetCamera}
        >
          <MyLocationIcon />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          minWidth: 200,
          display: "inline-grid",
          gridTemplateCollumns: "1fr",
          gridGap: "10px"
        }}
      >
        <Paper elevation={0}>
          <Typography variant="caption" style={{margin: 0}}>Frac Geometry Display</Typography>
          <ToggleButtonGroup
            value={hideFracGeometry}
            onChange={(e, newVal) => typeof newVal === 'boolean' && setHideFracGeometry(newVal)}
            fullWidth
            exclusive
            size="small"
          >
            <ToggleButton value={false}>Show</ToggleButton>
            <ToggleButton value={true}>Hide</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        <Paper>
          {
            isFDI !== true ?
              <FormControl fullWidth>
                <InputLabel id="MetricParameter">Metric Parameter</InputLabel>
                <Select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  size="small"
                  label="Metric Parameter"
                  labelId="MetricParameter"
                >
                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    Stage Information
                  </Typography>
                  {allMetrics['Stage Information']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />
                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    Pumping Information
                  </Typography>
                  {allMetrics['Pumping Information']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />

                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    ISIP
                  </Typography>
                  {allMetrics['ISIP']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />

                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    Stepdown Model
                  </Typography>
                  {allMetrics['Stepdown Model']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />

                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    Frac Model
                  </Typography>
                  {allMetrics['Frac Model']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />

                  <Typography
                    variant="caption"
                    style={{ marginLeft: 10 }}
                  >
                    FDI
                  </Typography>
                  {allMetrics['FDI']?.map((m) => (
                    <MenuItem
                      value={m}
                      key={m}
                    >
                      {m}
                    </MenuItem>
                  ))}
                  <Divider />

                </Select>
              </FormControl>
              : null
          }
        </Paper>
        
        <Paper>
          <FormControl fullWidth>
            <InputLabel id="VerticalWellboreScale">Vertical Scale</InputLabel>
            <Select
              labelId="VerticalWellboreScale"
              label="Vertical Wellbore Scale"
              value={verticalScaleMultiplier}
              onChange={(event) => {
                setVerticalScaleMultiplier(event.target.value);
                resetCamera();
              }}
              size="small"
            >
              <MenuItem value={1}>100%</MenuItem>
              <MenuItem value={1 / 5}>500%</MenuItem>
              <MenuItem value={1 / 10}>1000%</MenuItem>
              <MenuItem value={1 / 15}>1500%</MenuItem>
              <MenuItem value={1 / 30}>3000%</MenuItem>
              <MenuItem value={1 / 50}>5000%</MenuItem>
            </Select>
          </FormControl>
        </Paper>
        
        {
          // !hideFracGeometry && <Paper>
          // <FormControl fullWidth>
          //   <InputLabel id="VerticalScale">Vertical Scale</InputLabel>
          //   <Select
          //     labelId="VerticalScale"
          //     label="Vertical Scale"
          //     value={heightMultiplier}
          //     onChange={(event) => {
          //       setHeightMultiplier(event.target.value);
          //     }}
          //     size="small"
          //   >
          //     <MenuItem value={1}>100%</MenuItem>
          //     <MenuItem value={5}>500%</MenuItem>
          //     <MenuItem value={10}>1000%</MenuItem>
          //     <MenuItem value={15}>1500%</MenuItem>
          //   </Select>
          // </FormControl>
        // </Paper>
        }
        {!hideFracGeometry && <Paper>
          <FormControl fullWidth>
            <InputLabel id="HorizontalScale">Horizontal Scale</InputLabel>
            <Select
              labelId="HorizontalScale"
              label="Horizontal Scale"
              value={widthMultiplier}
              onChange={(event) => {
                setWidthMultiplier(event.target.value);
              }}
              size="small"
            >
              <MenuItem value={0.25}>25%</MenuItem>
              <MenuItem value={0.5}>50%</MenuItem>
              <MenuItem value={0.75}>75%</MenuItem>
              <MenuItem value={1}>100%</MenuItem>
            </Select>
          </FormControl>
        </Paper>}
        {
          isFDI !== true ?
            <Paper>
              <RTFOWellbore3DUISeismicityLegend seismicityFilter={seismicityFilter} setSeismicityFilter={setSeismicityFilter} darkMode={darkMode} />
            </Paper>
            : null
        }
      </Box>
      {
        isFDI !== true ?
          <RTFOWellboreUIColorScaleVisualization colorScale={colorScale} range={range} metric={metric} units={units} darkMode={darkMode} />
          : null
      }
    </>
  );
};

const RTFOWellbore3DUISeismicityLegend = ({ seismicityFilter, setSeismicityFilter, darkMode }) => {
  return (
    <div style={{ width: '100%', height: 'auto', backgroundColor: !darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.4)', borderRadius: '5px', padding: 10 }}>
      <Typography variant="caption" fontWeight="bold">Seismicity Legend</Typography>

      {[0.75, 1.5, 2.5, 3.5, 4.5].map(mag => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: '5px',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: seismicityFilter.includes(mag) ? 0.25 : 1
            }}
            onClick={() => {
              seismicityFilter.includes(mag)
                ? setSeismicityFilter(prev => prev.filter(d => d !== mag))
                : setSeismicityFilter(prev => [...prev, mag]);
            }}
            key={mag}
          >
            <div style={{
              width: '15px',
              height: '15px',
              marginRight: '5px',
              borderRadius: '3px',
              boxShadow: '0px 0px 4px lightgrey',
              backgroundColor: getSeismicEventColor(mag)
            }}></div>
            <Typography style={{ fontSize: '12px' }}>
              {mag === 0.75
                ? 'Local Magnitude < 1'
                : mag === 1.5
                  ? 'Local Magnitude 1 - 2'
                  : mag === 2.5
                    ? 'Local Magnitude 2 - 3'
                    : mag === 3.5
                      ? 'Local Magnitude 3 - 4'
                      : mag === 4.5
                        ? 'Local Magnitude > 4'
                        : ''}
            </Typography>
          </div>
        )
      })}
    </div>
  );
};
